app.controller('uspsMailingsController', function ($rootScope, $scope, $http, $location, safeApply) {

  /*
    USPS Mailings - System Tools

    Printed mailers (API server/modules/mailer) printed and mailed by LOB. Three views:
      new     - pick a template, Preview (who qualifies + the first letter), Create Batch.
                The API builds each client's PDF (the letter, plus an invoice if the template attaches one), stores it in GCS and
                writes one z_mailer row per piece. Built with log_messages false, so nothing
                reaches a client's message history yet.
      batches - every z_mailer batch, newest first.
      batch   - one batch's pieces, and Send to LOB:
                  Test - nothing printed or charged; the letters show in the LOB dashboard's
                         Test view with a proof.
                  Live - printed + mailed, logged to each client's message history. Needs
                         SEND typed to confirm.
                Verify Addresses - LOB's US address check (live key, charged per address).
                         A live send skips rows marked undeliverable.
    Costs are estimates from the LOB plan rates in the API's apiConfig.js (keys.lob.pricing) -
    LOB does not return a price; its dashboard -> Billing has the real charges.

    API: /api/mailer/templates | render | packet | batches | lob/send
  */

  const host = $location.$$host == 'localhost' ? 'http://localhost:5050' : 'https://api.propertytax.io'

  // Inputs the panel sets itself, or too technical to show
  const HIDDEN_INPUTS = ['mailer_id', 'is_preview', 'save_preview', 'log_messages', 'qr_size']

  // Friendly labels for the template schema inputs - anything not listed is title-cased
  const LABELS = {
    tax_year                    : { label: 'Tax Year' },
    user_id                     : { label: 'Single Client', hint: 'User id - 0 for all' },
    row_limit                   : { label: 'Max Letters', hint: '0 for no limit' },
    min_amount_due              : { label: 'Min Balance', hint: 'Dollars' },
    age_min                     : { label: 'Min Days Past Due' },
    age_max                     : { label: 'Max Days Past Due' },
    sent_less_than              : { label: 'Mail Each Tier', hint: 'Times - 0 for no cap' },
    response_days               : { label: 'Response Days' },
    no_email_only               : { label: 'No email on file only' },
    ignore_email_on_file        : { label: 'Ignore email on file' },
    include_zero_dollar_invoices: { label: 'Include $0 invoices' },
    embed_qr                    : { label: 'Include QR code' }
  }

  $scope.view = 'new'
  $scope.templates = []
  $scope.form = { template: null, numbers: [], toggles: [], values: {} }
  $scope.preview = { loading: false, error: null, data: null }
  $scope.build = { loading: false, error: null, data: null }
  $scope.batches = { loading: false, error: null, rows: [] }
  $scope.batch = { loading: false, error: null, id: null, info: null, pieces: [], open: {} }
  $scope.send = { mode: 'test', loading: false, error: null, data: null, confirming: false, confirmText: '' }
  $scope.verify = { loading: false, error: null, data: null }
  $scope.pricing = null

  $rootScope.$watch('user_info', function () {
    $scope.user_info = $rootScope.user_info
  })

  init()
  async function init() {
    loadBatches()
    try {
      const res = await post('/api/mailer/templates', {})
      $scope.templates = res.templates || []
      $scope.pricing = res.pricing || null
      if ($scope.templates.length) $scope.selectTemplate($scope.templates[0])
    } catch (error) {
      $scope.build.error = 'Could not load mailer templates - ' + error.message
    }
    safeApply($scope)
  }

  $scope.setView = function (view) {
    $scope.view = view
    if (view == 'batches') loadBatches()
    if (view == 'new') setTimeout(fitPreview)   // a resize while hidden left the old scale
  }

  // -- new batch --------------------------------------------------------------
  $scope.selectTemplate = function (template) {
    // verify_lob is the panel's own option, not a template input - checks every address in the new
    // batch with LOB right after Create Batch, and adds that charge to the preview's Est. Cost
    $scope.form = { template: template, numbers: [], toggles: [], values: {}, verify_lob: true }
    Object.entries(template.inputs || {})
      .filter(([name]) => !HIDDEN_INPUTS.includes(name))
      .forEach(([name, def]) => {
        const meta = LABELS[name] || {}
        const field = {
          name : name,
          label: meta.label || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          hint : meta.hint || (def.default !== undefined && def.type != 'boolean' ? 'Default ' + def.default : ''),
          min  : def.min !== undefined ? def.min : null    // the API rejects less (validate_inputs.js)
        }
        $scope.form.values[name] = def.default !== undefined ? def.default : (name == 'tax_year' ? $rootScope.tax_year : null)
        if (def.type == 'boolean') $scope.form.toggles.push(field)
        else $scope.form.numbers.push(field)
      })
    $scope.preview = { loading: false, error: null, data: null }
    $scope.build = { loading: false, error: null, data: null }
  }

  function batchParams() {
    const params = { mailer_id: $scope.form.template.mailer_id }
    Object.entries($scope.form.values).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params[k] = v
    })
    return params
  }

  // Min-balance levels for the preview's breakdown table: the chosen min +/- $15 in $5 steps
  // ($25 -> 10, 15 ... 40). Never below the template's floor - $5, never $0 (schema.js min).
  // The API counts them all from one query.
  const BREAKDOWN_STEP = 5
  const BREAKDOWN_SPAN = 3
  const MIN_BALANCE_FLOOR = 5
  function minBalanceFloor() {
    const field = $scope.form.numbers.find(f => f.name == 'min_amount_due')
    return field && field.min !== null ? Math.max(field.min, MIN_BALANCE_FLOOR) : MIN_BALANCE_FLOOR
  }
  function breakdownLevels(min) {
    const floor = minBalanceFloor()
    const levels = []
    for (let i = -BREAKDOWN_SPAN; i <= BREAKDOWN_SPAN; i++) {
      const level = Number(min) + i * BREAKDOWN_STEP
      if (level >= floor) levels.push(level)
    }
    return levels
  }

  // A number field below its min - caught here so Preview / Create Batch say why
  function belowMin() {
    const field = $scope.form.numbers.find(f => {
      const v = $scope.form.values[f.name]
      const floor = f.name == 'min_amount_due' ? minBalanceFloor() : f.min
      return floor !== null && v !== null && v !== undefined && v !== '' && Number(v) < floor
    })
    if (!field) return null
    const floor = field.name == 'min_amount_due' ? minBalanceFloor() : field.min
    return field.label + ' must be at least ' + (field.name == 'min_amount_due' ? '$' + floor.toFixed(2) : floor) + '.'
  }

  $scope.runPreview = async function () {
    const invalid = belowMin()
    if (invalid) {
      $scope.preview = { loading: false, error: invalid, data: null }
      return
    }
    $scope.preview = { loading: true, error: null, data: null }
    safeApply($scope)
    try {
      const params = Object.assign(batchParams(), { is_preview: true })
      const min = params.min_amount_due
      if (min !== undefined && min !== null && !isNaN(Number(min))) params.breakdown = breakdownLevels(min)

      const data = await post('/api/mailer/render', params)
      const first = data.rows && data.rows[0]
      $scope.preview.data = {
        row_count: data.row_count,
        tax_year : data.tax_year,
        first    : first ? first.data.client_name : null,
        min      : Number(min),
        breakdown: data.breakdown || null,
        on_file  : data.addresses_on_file || 0   // already checked by LOB - reused, never charged again
      }
      // Compare against the selected level's own count - row_count is capped by Max Letters
      const selected = (data.breakdown || []).find(b => b.min_amount_due == Number(min))
      $scope.preview.data.selected_count = selected ? selected.count : data.row_count
      // Past-due dollars each level adds or drops vs. the selected one (in cents, so no float drift)
      ;(data.breakdown || []).forEach(b => {
        b.due_diff = selected && b.total_due != null && selected.total_due != null
          ? (Math.round(b.total_due * 100) - Math.round(selected.total_due * 100)) / 100
          : null
      })
      safeApply($scope)
      // srcdoc keeps the letter's own <style> away from the CRM's stylesheets
      const frame = document.getElementById('uspsPreviewFrame')
      if (frame) frame.srcdoc = first ? PRINT_VIEW + first.html : ''
      setTimeout(fitPreview)
    } catch (error) {
      $scope.preview.error = error.message
    }
    $scope.preview.loading = false
    safeApply($scope)
  }

  // "Use this value" in the breakdown table - set Min Balance and preview again, so the table
  // re-centres on the new value
  $scope.useMinBalance = function (level) {
    $scope.form.values.min_amount_due = level
    $scope.runPreview()
  }

  $scope.createBatch = async function () {
    const invalid = belowMin()
    if (invalid) {
      $scope.build = { loading: false, error: invalid, data: null }
      return
    }
    const count = $scope.preview.data ? ' (' + $scope.preview.data.row_count + ')' : ''
    const verifyLob = !!$scope.form.verify_lob
    if (!confirm('Build PDFs for every qualifying client' + count + ' and store them in GCS?\n\n' +
      (verifyLob
        ? "New addresses are then checked with LOB's address verification (live key - LOB charges per address). Addresses LOB already checked reuse that result for free.\n\n"
        : 'Addresses LOB already checked get that result (free). No new addresses are checked - switch pieces on in the batch to check them.\n\n') +
      'Nothing is mailed until you send the batch to LOB.')) return
    $scope.build = { loading: true, error: null, data: null }
    safeApply($scope)
    let batch_id = null
    try {
      const data = await post('/api/mailer/packet', Object.assign(batchParams(), { log_messages: false }))
      $scope.build.data = data
      batch_id = data.batch_id
      await loadBatches()
    } catch (error) {
      $scope.build.error = error.message
    }
    $scope.build.loading = false
    safeApply($scope)
    if (!batch_id) return
    await $scope.openBatch(batch_id)
    // Always run verify - stored results apply for free. With the toggle off every piece is switched
    // off first, so nothing is charged. (Already confirmed above.)
    if (!verifyLob) {
      try {
        await post('/api/mailer/lob/verify_flag', { batch_id: batch_id, verify: false })
      } catch (error) {
        $scope.verify.error = 'Could not switch verification off - nothing was checked. ' + error.message
        safeApply($scope)
        return
      }
    }
    verifyBatch()
  }

  // -- batches ----------------------------------------------------------------
  async function loadBatches() {
    $scope.batches.loading = true
    $scope.batches.error = null
    safeApply($scope)
    try {
      const data = await post('/api/mailer/batches', { limit: 50 })
      $scope.batches.rows = data.batches || []
    } catch (error) {
      $scope.batches.error = error.message
    }
    $scope.batches.loading = false
    safeApply($scope)
  }
  $scope.loadBatches = loadBatches

  $scope.openBatch = async function (batch_id) {
    $scope.view = 'batch'
    $scope.batch = { loading: true, error: null, id: batch_id, info: $scope.batches.rows.find(b => b.batch_id == batch_id) || null, pieces: [], open: {} }
    $scope.send = { mode: 'test', loading: false, error: null, data: null, confirming: false, confirmText: '' }
    $scope.verify = { loading: false, error: null, data: null }
    safeApply($scope)
    await loadPieces()
  }

  $scope.closeBatch = function () {
    $scope.setView('batches')
  }

  async function loadPieces() {
    $scope.batch.loading = true
    safeApply($scope)
    try {
      const data = await post('/api/mailer/batches', { batch_id: $scope.batch.id })
      $scope.batch.pieces = data.pieces || []
      $scope.batch.eventsError = data.events_error || null
    } catch (error) {
      $scope.batch.error = error.message
    }
    $scope.batch.loading = false
    safeApply($scope)
  }
  $scope.refreshBatch = loadPieces

  // -- webhook events ---------------------------------------------------------
  // Each piece's row opens to the LOB webhook events stored for its letter. Open rows are kept by
  // z_mailer id, so they stay open across a refresh.
  $scope.toggleEvents = function (piece) {
    $scope.batch.open[piece.id] = !$scope.batch.open[piece.id]
  }
  $scope.allOpen = function () {
    return $scope.batch.pieces.length > 0 && $scope.batch.pieces.every(p => $scope.batch.open[p.id])
  }
  $scope.setAllOpen = function (open) {
    $scope.batch.open = {}
    if (open) $scope.batch.pieces.forEach(p => { $scope.batch.open[p.id] = true })
  }
  $scope.eventCount = function () {
    return $scope.batch.pieces.reduce((sum, p) => sum + (p.events ? p.events.length : 0), 0)
  }

  // 'letter.certified.in_local_area' -> 'In Local Area', with certified / return envelope flagged
  $scope.eventLabel = function (event) {
    const type = event.event_type || ''
    const name = type.replace(/^letter\./, '').replace(/^(certified|return_envelope)\./, '')
    return {
      name     : name.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown',
      certified: /^letter\.certified\./.test(type),
      envelope : /^letter\.return_envelope\./.test(type)
    }
  }

  // Stat tiles on the batch view
  $scope.batchStats = function () {
    const pieces = $scope.batch.pieces
    const pages = pieces.reduce((sum, p) => sum + (Number(p.page_count) || 0), 0)
    return {
      pieces       : pieces.length,
      pages        : pages,
      cost         : $scope.estimate(pieces.length, pages),
      test         : pieces.filter(p => p.lob_status == 'submitted' && p.lob_mode == 'test').length,
      live         : pieces.filter(p => p.lob_status == 'submitted' && p.lob_mode == 'live').length,
      errors       : pieces.filter(p => p.lob_status == 'error').length,
      verified     : pieces.filter(p => p.lob_verified_at).length,
      undeliverable: pieces.filter(p => p.lob_deliverability == 'undeliverable').length,
      unitIssues   : pieces.filter(p => p.lob_deliverability == 'deliverable_missing_unit' || p.lob_deliverability == 'deliverable_incorrect_unit').length
    }
  }

  // Pieces a live send would actually mail - mirrors the skip rules in the API's lob_send.js
  function liveQueue() {
    return $scope.batch.pieces.filter(p =>
      p.status != 'void' && p.pdf_url && !(p.lob_mode == 'live' && p.lob_letter_id) && p.lob_deliverability != 'undeliverable')
  }
  $scope.liveCount = function () { return liveQueue().length }
  $scope.livePages = function () { return liveQueue().reduce((sum, p) => sum + (Number(p.page_count) || 0), 0) }
  $scope.liveCost = function () { return $scope.estimate($scope.liveCount(), $scope.livePages()) }

  // -- cost -------------------------------------------------------------------
  // letters x per_letter, plus every page past each letter's first. null when no rates are set.
  $scope.estimate = function (letters, pages) {
    const rate = $scope.pricing
    if (!rate || !letters) return letters ? null : 0
    const extra = Math.max(0, (Number(pages) || letters) - letters)
    return letters * rate.per_letter + extra * (rate.extra_page || 0)
  }

  // New-batch preview: the letters plus, with Verify addresses on, one LOB address check per letter
  // whose address is not on file (on_file - LOB already checked it, reused free). Charges every new
  // address - the plan's free monthly checks are not counted (usage is not known here).
  $scope.verifyCost = function (letters, onFile) {
    const rate = $scope.pricing
    const checks = Math.max(0, letters - (onFile || 0))
    return $scope.form.verify_lob && rate && rate.verify_address ? checks * rate.verify_address : 0
  }
  $scope.previewEstimate = function (letters, onFile) {
    const cost = $scope.estimate(letters, letters)
    return cost === null ? null : cost + $scope.verifyCost(letters, onFile)
  }

  // -- verify addresses -------------------------------------------------------
  // What Verify Addresses would do with each non-void piece - mirrors the order in the API's lob_verify.js
  $scope.verifyPlan = function () {
    const plan = { charge: 0, onFile: 0, off: 0, done: 0, on: 0 }
    $scope.batch.pieces.forEach(p => {
      if (p.status == 'void') return
      if (p.lob_verify) plan.on++
      if (p.lob_verified_at) plan.done++
      else if (p.lob_on_file) plan.onFile++
      else if (p.lob_verify) plan.charge++
      else plan.off++
    })
    plan.cost = $scope.pricing && $scope.pricing.verify_address ? plan.charge * $scope.pricing.verify_address : null
    return plan
  }

  $scope.runVerify = function () {
    const plan = $scope.verifyPlan()
    // Nothing to pay for -> just apply what is on file
    if (plan.charge && !confirm('Check ' + plan.charge + ' new address' + (plan.charge == 1 ? '' : 'es') + " with LOB's address verification" +
      (plan.cost !== null ? ' (about $' + plan.cost.toFixed(2) + ')' : '') + '?\n\n' +
      (plan.onFile ? plan.onFile + ' address' + (plan.onFile == 1 ? ' was' : 'es were') + ' already checked - reused free.\n' : '') +
      'This uses the LIVE key - LOB charges per address checked. Nothing is mailed.')) return
    verifyBatch(false)
  }

  // Check Again - pays for a fresh check of every switched-on piece, ignoring results on file
  $scope.runCheckAgain = function () {
    const plan = $scope.verifyPlan()
    if (!plan.on) return
    const cost = $scope.pricing && $scope.pricing.verify_address ? ' (about $' + (plan.on * $scope.pricing.verify_address).toFixed(2) + ')' : ''
    if (!confirm('Check all ' + plan.on + ' switched-on address' + (plan.on == 1 ? '' : 'es') + ' with LOB again' + cost + '?\n\n' +
      'Results on file are ignored and every one is charged. Use it when an address has changed or a result looks stale.')) return
    verifyBatch(true)
  }

  // The Verify switch on each piece - may Verify Addresses pay LOB to check it
  $scope.setVerify = async function (piece, on) {
    const before = piece.lob_verify
    piece.lob_verify = on ? 1 : 0
    try {
      await post('/api/mailer/lob/verify_flag', { batch_id: $scope.batch.id, ids: [piece.id], verify: !!on })
    } catch (error) {
      piece.lob_verify = before
      $scope.batch.error = 'Could not change Verify for ' + piece.recipient_name + ' - ' + error.message
    }
    safeApply($scope)
  }
  $scope.allVerify = function () {
    const pieces = $scope.batch.pieces.filter(p => p.status != 'void')
    return pieces.length > 0 && pieces.every(p => p.lob_verify)
  }
  $scope.setVerifyAll = async function (on) {
    const before = $scope.batch.pieces.map(p => p.lob_verify)
    $scope.batch.pieces.forEach(p => { p.lob_verify = on ? 1 : 0 })
    try {
      await post('/api/mailer/lob/verify_flag', { batch_id: $scope.batch.id, verify: !!on })
    } catch (error) {
      $scope.batch.pieces.forEach((p, i) => { p.lob_verify = before[i] })
      $scope.batch.error = 'Could not change Verify for the batch - ' + error.message
    }
    safeApply($scope)
  }

  async function verifyBatch(force) {
    $scope.verify = { loading: true, error: null, data: null }
    safeApply($scope)
    try {
      $scope.verify.data = await post('/api/mailer/lob/verify', { batch_id: $scope.batch.id, force: !!force })
    } catch (error) {
      $scope.verify.error = error.message
    }
    $scope.verify.loading = false
    await loadPieces()
    loadBatches()
  }

  const DELIVERABILITY = {
    deliverable                 : { label: 'Deliverable', css: 'um-badge-live' },
    deliverable_unnecessary_unit: { label: 'Deliverable', css: 'um-badge-live', note: 'Unit not needed' },
    deliverable_missing_unit    : { label: 'Missing unit', css: 'um-badge-warn', note: 'Needs an apt / suite number' },
    deliverable_incorrect_unit  : { label: 'Wrong unit', css: 'um-badge-warn', note: 'Apt / suite number not found' },
    undeliverable               : { label: 'Undeliverable', css: 'um-badge-error', note: 'Skipped on a live send' },
    error                       : { label: 'Unreadable', css: 'um-badge-error' }
  }
  $scope.addressCheck = function (piece) {
    return piece.lob_deliverability ? (DELIVERABILITY[piece.lob_deliverability] || { label: piece.lob_deliverability, css: '' }) : null
  }

  // -- send to LOB ------------------------------------------------------------
  $scope.setMode = function (mode) {
    $scope.send.mode = mode
    $scope.send.confirming = false
    $scope.send.confirmText = ''
  }

  $scope.startSend = function () {
    if ($scope.send.mode == 'live') {
      $scope.send.confirming = true
      $scope.send.confirmText = ''
      return
    }
    sendToLob()
  }

  $scope.confirmLive = function () {
    if ($scope.send.confirmText !== 'SEND') return
    $scope.send.confirming = false
    sendToLob()
  }

  async function sendToLob() {
    $scope.send.loading = true
    $scope.send.error = null
    $scope.send.data = null
    safeApply($scope)
    try {
      const user = $scope.user_info || {}
      $scope.send.data = await post('/api/mailer/lob/send', {
        batch_id: $scope.batch.id,
        mode    : $scope.send.mode,
        sent_by : [user.first_name, user.last_name].filter(Boolean).join(' ') || String(user.id || '')
      })
    } catch (error) {
      $scope.send.error = error.message
    }
    $scope.send.loading = false
    await loadPieces()
    loadBatches()
  }

  // -- preview sheet ----------------------------------------------------------
  // The preview is the letter's HTML, which still carries both addresses for the message-center
  // copy. LOB prints the addresses on the real letter (the PDF leaves that area blank), so the
  // preview hides ours and the sheet marks LOB's area instead. body margin 0 - the browser's
  // default 8px made the 8.5in page wider than the frame and scroll sideways.
  const PRINT_VIEW = '<style>html,body{margin:0;overflow:hidden}.return-address,.recipient-address{visibility:hidden}</style>'
  const SHEET_W = 816   // 8.5in at 96px/in - the iframe is drawn at full size and scaled down

  function fitPreview() {
    const sheet = document.querySelector('#usps-mailings-page .um-sheet')
    const frame = document.getElementById('uspsPreviewFrame')
    if (!sheet || !frame || !sheet.clientWidth) return
    frame.style.transform = 'scale(' + (sheet.clientWidth / SHEET_W) + ')'
  }
  window.addEventListener('resize', fitPreview)
  $scope.$on('$destroy', () => window.removeEventListener('resize', fitPreview))

  // -- helpers ----------------------------------------------------------------
  // The mailer API answers { success, data } or { success: false, error }
  async function post(path, body) {
    const res = await $http.post(host + path, body)
    if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || 'No response from ' + path)
    return res.data.data
  }

  $scope.lobLabel = function (piece) {
    if (!piece.lob_status) return ''
    return piece.lob_status == 'error' ? 'Error' : (piece.lob_mode == 'live' ? 'Live' : 'Test')
  }
})
